import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { auth } from '../firebase';

export default function DataEnrichment() {
  const [showUploader, setShowUploader] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();

  const handleShowUploader = () => {
    setShowUploader(true);
    setSuccess(null);
    setError(null);
    setSummary(null);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setSuccess(null);
    setError(null);
    setSummary(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setSuccess(null);
    setError(null);
    setSummary(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('User not authenticated. Please log in again.');
        setUploading(false);
        return;
      }
      const idToken = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE_URL}/upload_candidates`, formData, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess(true);
      setSummary(response.data.summary || 'Upload complete.');
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
      setSuccess(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <button
        className="mb-6 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
        onClick={() => navigate('/dashboard')}
      >
        &larr; Back to Dashboard
      </button>
      <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Enrich people via CSV</h1>
      <div className="mb-8">
        <button
          className="px-6 py-3 bg-green-600 text-white rounded-md font-semibold text-lg hover:bg-green-700 transition"
          onClick={handleShowUploader}
        >
          Enrich People
        </button>
      </div>
      {showUploader && (
        <form onSubmit={handleUpload} className="mb-8 flex flex-col gap-4">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 dark:text-gray-200"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            disabled={uploading || !file}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </form>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded">Upload successful! {summary}</div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded">Error: {error}</div>
      )}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Instructions</h2>
        <ol className="list-decimal ml-6 mb-4 text-gray-700 dark:text-gray-200">
          <li>Download the sample CSV template or prepare your own file.</li>
          <li>Ensure your CSV has <span className="font-semibold">all the following columns</span>:</li>
        </ol>
        <div className="bg-gray-100 dark:bg-gray-900 rounded p-4 mb-4 text-sm font-mono overflow-x-auto">
          contactName,email,job_title,Location,company,linkedin_url,phoneNumbers,Skills,work_1_title,work_1_company,work_1_years,work_2_title,work_2_company,work_2_years
        </div>
        <ul className="list-disc ml-6 text-gray-700 dark:text-gray-200 mb-4">
          <li>You can add more work experience columns as needed (e.g., work_3_title, work_3_company, work_3_years, ...).</li>
          <li>phoneNumbers and Skills can be comma-separated lists.</li>
          <li>Each row represents one candidate.</li>
        </ul>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Example row:<br/>
          John Smith,john@example.com,Software Engineer,London,Acme Corp,https://linkedin.com/in/johnsmith,"+441234567890","Python,Django",Developer,Acme Corp,2,Engineer,Beta Inc,1
        </div>
      </div>
    </div>
  );
}
